import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Link, RouteComponentProps } from 'react-router-dom';
import { Button, ButtonGroup, Col, Label, Row } from 'reactstrap';
import { AvField, AvForm, AvGroup, AvInput } from 'availity-reactstrap-validation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IRootState } from 'app/shared/reducers';
import { useTranslation } from 'app/shared/util/translation-utils';
import { getEntity, reset as resetPatientBaby } from './patient-baby.reducer';
import {
  reset as resetPatientCase,
  updateEntity as updatePatientCase
} from 'app/entities/patient-case/patient-case.reducer';
import { getEntity as getPatient, reset as resetPatient } from 'app/entities/patient/patient.reducer';
import { IPatientCase } from 'app/shared/model/patient-case.model';
import {
  getMasterEntitiesByEnumNames,
  getMasterEntitiesByGroupNames
} from 'app/entities/watotocare-entity/watotocare-entity.reducer';
import { convertDateTimeFromServer, convertDateTimeToServer, daysSince } from 'app/shared/util/date-utils';
import { IWatotoCareEntityMaster } from 'app/shared/model/watotocare-entity-master.model';
import { IWatotoCareEntityDto } from 'app/shared/model/watotocare-entity-dto.model';
import WatotocareEntityFormGroup from 'app/shared/components/watotocare-entity-form/watotocare-entity-form-group';

export interface IPatientBabyDischargeProps extends StateProps, DispatchProps, RouteComponentProps<{
  parentId: string,
  id: string
}> {
}

export const PatientBabyDischarge = (props: IPatientBabyDischargeProps) => {
  const [isNew, setIsNew] = useState(null);
  const {
    patientBabyEntity,
    patientEntity,
    watotoCareEntityList,
    watotoCareEntitiesByGroup,
    loading,
    updating,
    currentLocale
  } = props;
  const { t } = useTranslation();
  const [patientCase, setPatientCase] = useState({} as IPatientCase);
  const [aliveStatus, setAliveStatus] = useState(null);
  const [babyAliveStatus, setBabyAliveStatus] = useState(null);
  const [hivStatus, setHivStatus] = useState(null);
  const [babyDischargeConditionEntity, setBabyDischargeConditionEntity] = useState<IWatotoCareEntityMaster>(null);
  const [patientDischargeMasterEntities, setPatientDischargeMasterEntities] = useState<IWatotoCareEntityMaster[]>([]);
  const [entityFormInitialValues, setEntityFormInitialValues] = useState<IWatotoCareEntityDto[]>([]);
  const [entityFormSelectedValues, setEntityFormSelectedValues] = useState<IWatotoCareEntityDto[]>([]);

  const WATOTOCARE_ENTITY_ENUM_NAMES = ['BABY_DISCHARGE_CONDITION'];
  const WATOTOCARE_ENTITY_GROUP_NAME = 'PATIENT_DISCHARGE';

  useEffect(() => {
    props.getEntity(props.match.params.id);
  }, []);

  useEffect(() => {
    if (patientBabyEntity.patientId) {
      props.getPatient(patientBabyEntity.patientId);
    }
    if (patientBabyEntity.patientCase) {
      if (patientBabyEntity.patientCase.babyDischargedAt) {
        setIsNew(false);
      } else {
        setIsNew(true);
      }
      setPatientCase(patientBabyEntity.patientCase);
      setHivStatus(patientBabyEntity.patientCase.patientHasHiv);
      setAliveStatus(patientBabyEntity.patientCase.patientIsAlive);
      setBabyAliveStatus(patientBabyEntity.patientCase.patientBabyIsAlive);
      setEntityFormInitialValues(patientBabyEntity.patientCase.patientDischargeEntities);
    }
  }, [patientBabyEntity]);

  useEffect(() => {
    if (patientEntity.id) {
      props.getMasterEntitiesByEnumNames(WATOTOCARE_ENTITY_ENUM_NAMES, patientEntity.countryId ? patientEntity.countryId : props.userCountryId);
      props.getMasterEntitiesByGroupNames([WATOTOCARE_ENTITY_GROUP_NAME], patientEntity.countryId);
    }
  }, [patientEntity]);

  useEffect(() => {
    if (watotoCareEntityList.length > 0) {
      const babyDischargeConditionMasterEntity = watotoCareEntityList.find(entity => entity.enumName === 'BABY_DISCHARGE_CONDITION');
      if (babyDischargeConditionMasterEntity) {
        setBabyDischargeConditionEntity(babyDischargeConditionMasterEntity);
      }
    }
  }, [watotoCareEntityList]);

  useEffect(() => {
    if (Object.keys(watotoCareEntitiesByGroup).length > 0) {
      setPatientDischargeMasterEntities(watotoCareEntitiesByGroup[WATOTOCARE_ENTITY_GROUP_NAME]);
    }
  }, [watotoCareEntitiesByGroup]);


  useEffect(() => {
    if (props.updateSuccess) {
      if (isNew) {
        props.history.push(`/patient-visit/create?patientBabyId=${patientBabyEntity.id}`);
      } else {
        props.history.push('/patient');
      }
    }
  }, [props.updateSuccess]);


  const saveEntity = (event, errors, values) => {
    if (errors.length > 0) return;

    // convert discharge datetime to UTC
    values.babyDischargedAt = convertDateTimeToServer(values.babyDischargedAt);

    if (errors.length === 0) {
      const entity = {
        ...patientCase,
        ...values,
        patientIsAlive: aliveStatus,
        patientBabyIsAlive: babyAliveStatus,
        patientHasHiv: hivStatus,
        patientDischargeEntityIds: entityFormSelectedValues
      };

      props.updatePatientCase(entity);
    }
  };

  const resetForm = () => {
    props.resetPatient();
    props.resetPatientBaby();
    props.resetPatientCase();
    setPatientCase([] as IPatientCase);
    setAliveStatus(null);
    setBabyAliveStatus(null);
    setHivStatus(null);
    setEntityFormInitialValues([]);
  };

  useEffect(() => () => {
    resetForm();
  }, []);

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="watotocareApp.patient.baby.discharge.createOrEditLabel">
            {isNew ? <>{t('patientBaby.discharge.createLabel')}</> : <>{t('patientBaby.discharge.editLabel')}</>}
          </h2>
        </Col>
      </Row>
      <br />
      <Row className="justify-content-center">
        <Col md="4">
          <h5 className="text-primary">{t('common.fields.motherDetails')}</h5>
          <div>
            {t('common.fields.watotoCareId')}: {patientEntity.wcId}
            <br />
            {t('common.fields.motherName')}: {`${patientEntity.firstName} ${patientEntity.lastName}`}
          </div>
        </Col>
        <Col md="4">
          <h5 className="text-primary">{t('common.fields.babyDetails')}</h5>
          <div>
            {t('common.fields.dateOfBirth')}: {patientBabyEntity.birthDate} ({t('common.fields.ageInDays')}: {daysSince(patientBabyEntity.birthDate)})
            <br />
            {t('common.fields.babyName')}: {patientBabyEntity.firstName ? `${patientBabyEntity.firstName} ${patientBabyEntity.lastName}` : '-'}
          </div>
        </Col>
      </Row>
      <br />
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>{t('common.loading')}</p>
          ) : (
            <AvForm model={isNew ? {} : patientCase} onSubmit={saveEntity}>

              <div className="mt-3">
                <h5 className="text-primary">{t('patientBaby.discharge.motherDetails')}</h5>
                <br />
                <Row className="justify-content-left">
                  <Col md="12">
                    <AvGroup>
                      <Label id="aliveStatus" for="discharge-aliveStatus">
                        {t('patientBaby.discharge.isAlive')}
                      </Label>
                      <ButtonGroup style={{ marginLeft: '30px' }} id="discharge-aliveStatus">
                        <Button
                          color={aliveStatus === true ? 'primary' : 'secondary'}
                          onClick={() => {
                            setAliveStatus(true);
                          }}
                        >
                          {t('common.yes')}
                        </Button>

                        <Button
                          color={aliveStatus === false ? 'primary' : 'secondary'}
                          onClick={() => {
                            setAliveStatus(false);
                          }}
                        >
                          {t('common.no')}
                        </Button>
                      </ButtonGroup>
                      <AvField
                        name="aliveStatus"
                        type="hidden"
                        value={aliveStatus === null ? null : aliveStatus.toString()}
                        validate={{
                          required: { value: true, errorMessage: t('common.validation.required') }
                        }}
                      />
                    </AvGroup>
                  </Col>
                  <Col md="12" className="mt-3">
                    <AvGroup>
                      <Label id="hivStatus">
                        {t('patientBaby.discharge.hivStatus')}
                      </Label>
                      <ButtonGroup style={{ marginLeft: '30px' }}>
                        <Button
                          color={hivStatus === true ? 'primary' : 'secondary'}
                          onClick={() => {
                            setHivStatus(true);
                          }}
                        >
                          {t('patientBaby.discharge.positive')}
                        </Button>

                        <Button
                          color={hivStatus === false ? 'primary' : 'secondary'}
                          onClick={() => {
                            setHivStatus(false);
                          }}
                        >
                          {t('patientBaby.discharge.negative')}
                        </Button>
                      </ButtonGroup>
                      <AvField
                        name="hivStatus"
                        type="hidden"
                        value={hivStatus === null ? null : hivStatus.toString()}
                        validate={{
                          required: { value: true, errorMessage: t('common.validation.required') }
                        }}
                      />
                    </AvGroup>
                  </Col>

                  <br />
                  <Col md="6" className="mt-3">
                    <AvGroup>
                      <Label id="patientDischargeNotes" for="discharge-patientDischargeNotes">
                        {t('patientBaby.discharge.dischargeNotes')}
                      </Label>
                      <AvField
                        id="discharge-patientDischargeNotes"
                        type="text"
                        name="patientDischargeNotes"
                        value={patientCase.patientDischargeNotes}
                      />
                    </AvGroup>
                  </Col>
                </Row>

                <br />
                <h5 className="text-primary">{t('patientBaby.discharge.babyDetails')}</h5>
                <br />
                <Row className="justify-content-left">
                  <Col md="12">
                    <AvGroup>
                      <Label id="babyAliveStatus" for="discharge-babyAliveStatus">
                        {t('patientBaby.discharge.isBabyAlive')}
                      </Label>
                      <ButtonGroup style={{ marginLeft: '30px' }} id="discharge-babyAliveStatus">
                        <Button
                          color={babyAliveStatus === true ? 'primary' : 'secondary'}
                          onClick={() => {
                            setBabyAliveStatus(true);
                          }}
                        >
                          {t('common.yes')}
                        </Button>

                        <Button
                          color={babyAliveStatus === false ? 'primary' : 'secondary'}
                          onClick={() => {
                            setBabyAliveStatus(false);
                          }}
                        >
                          {t('common.no')}
                        </Button>
                      </ButtonGroup>
                      <AvField
                        name="babyAliveStatus"
                        type="hidden"
                        value={babyAliveStatus === null ? null : babyAliveStatus.toString()}
                        validate={{
                          required: { value: true, errorMessage: t('common.validation.required') }
                        }}
                      />
                    </AvGroup>
                  </Col>

                  <Col md="6" className="mt-3">
                    {babyDischargeConditionEntity !== null &&
                      <AvGroup>
                        <Label id="babyDischargeCondition" for="discharge-babyDischargeCondition">
                          {currentLocale === 'en' ? babyDischargeConditionEntity.entityName : babyDischargeConditionEntity.stEntityName}
                        </Label>
                        <AvInput
                          id="discharge-babyDischargeCondition"
                          type="select"
                          name="babyDischargeCondition"
                          value={(patientCase.babyDischargeCondition) || ''}
                          validate={{
                            required: { value: true, errorMessage: t('common.validation.required') }
                          }}
                        >
                          {<option disabled selected value="">{t('common.selectOption')}</option>}
                          {babyDischargeConditionEntity
                            .watotoCareEntityDetails
                            .filter(entity => entity.isActive)
                            .sort((a, b) => a.displayOrder - b.displayOrder)
                            .map(val => (
                              <option value={val.id} key={val.id}>
                                {currentLocale === 'en' ? val.enDescription : val.stDescription}
                              </option>
                            ))}
                        </AvInput>
                      </AvGroup>
                    }
                  </Col>

                  <Col md="6" className="mt-3">
                    <AvGroup>
                      <Label id="babyDischargedAt" for="discharge-babyDischargedAt">
                        {t('patientBaby.discharge.dateAndTimeOfDischarge')}
                      </Label>
                      <AvField
                        id="discharge-babyDischargedAt"
                        type="datetime-local"
                        name="babyDischargedAt"
                        value={patientCase.babyDischargedAt ? convertDateTimeFromServer(patientCase.babyDischargedAt) : ''}
                        validate={{
                          required: { value: true, errorMessage: t('common.validation.required') }
                        }}
                      />
                    </AvGroup>
                  </Col>
                  <Col md="6">
                    <AvGroup>
                      <Label id="babyDischargeNotes" for="discharge-babyDischargeNotes">
                        {t('patientBaby.discharge.dischargeNotes')}
                      </Label>
                      <AvField
                        id="discharge-babyDischargeNotes"
                        type="text"
                        name="babyDischargeNotes"
                        value={patientCase.babyDischargeNotes}
                      />
                    </AvGroup>
                  </Col>
                </Row>

                <br />
                <h5 className="text-primary">{t('patientBaby.discharge.additionalDetails')}</h5>
                {patientDischargeMasterEntities && patientDischargeMasterEntities.length > 0 &&
                  <WatotocareEntityFormGroup
                    prefix="patient-discharge"
                    entities={patientDischargeMasterEntities}
                    initialValues={entityFormInitialValues}
                    onChange={(values) => setEntityFormSelectedValues(values)}
                  />
                }
              </div>

              <br />
              <Button tag={Link} id="cancel-save" to="/patient" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">{t('common.actions.back')}</span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp; {t('common.actions.save')}
              </Button>
            </AvForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

const mapStateToProps = (storeState: IRootState) => ({
  patientBabyEntity: storeState.patientBaby.entity,
  patientEntity: storeState.patient.entity,
  loading: storeState.patientBaby.loading,
  updating: storeState.patientCaseEntity.updating,
  updateSuccess: storeState.patientCaseEntity.updateSuccess,
  watotoCareEntityList: storeState.watotoCareEntity.masterEntities,
  watotoCareEntitiesByGroup: storeState.watotoCareEntity.masterEntitiesByGroup,
  userCountryId: storeState.authentication.userCountryId,
  currentLocale: storeState.locale.currentLocale
});

const mapDispatchToProps = {
  getEntity,
  getPatient,
  updatePatientCase,
  getMasterEntitiesByEnumNames,
  getMasterEntitiesByGroupNames,
  resetPatient,
  resetPatientBaby,
  resetPatientCase
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default connect(mapStateToProps, mapDispatchToProps)(PatientBabyDischarge);
